"use client"
import { create } from "zustand"

interface SharedData {
  destinations: any[]
  destination: any

  restaurants: any[]
  maisons: any[]
  weekends: any[]
  musees: any[]
  allEvents: any[]
  hotels: any[]

  loadingDestinations: boolean
  loadingDestination: boolean
  loadingRestaurants: boolean
  loadingMaisons: boolean
  loadingWeekends: boolean
  loadingMusees: boolean
  loadingAllEvents: boolean
  loadingAllHotels: boolean
  
  errorDestinations: string | null
  errorDestination: string | null
  errorRestaurants: string | null
  errorMaisons: string | null
  errorWeekends: string | null
  errorMusees: string | null
  errorAllEvents: string | null

  errorAllHotels: string | null

  fetchDestinations: () => Promise<void>
  fetchDestinationBySlug: (slug: string) => Promise<void>

  fetchRestaurantsBySlug: (slug: string) => Promise<void>
  fetchMaisonsBySlug: (slug: string) => Promise<void>
  fetchWeekendsBySlug: (slug: string) => Promise<void>
  fetchMuseesBySlug: (slug: string) => Promise<void>
  fetchAllEventsBySlug: (slug: string) => Promise<void>
  fetchAllHotelsBySlug: (slug: string) => Promise<void>

  fetchAllBySlug: (slug: string) => Promise<void>
}

export const useSharedData = create<SharedData>((set) => ({

  destinations: [],
  destination: null,
  hotels:[],

  restaurants: [],
  maisons: [],
  weekends: [],
  musees: [],
  allEvents: [],

  loadingDestinations: false,
  loadingDestination: false,
  loadingRestaurants: false,
  loadingMaisons: false,
  loadingWeekends: false,
  loadingMusees: false,
  loadingAllEvents: false,
  loadingAllHotels: false,

  errorDestinations: null,
  errorDestination: null,
  errorRestaurants: null,
  errorMaisons: null,
  errorWeekends: null,
  errorMusees: null,
  errorAllEvents: null,
  errorAllHotels: null,

  //------------------------------------
  // 📌 Fetch all destinations
  //------------------------------------
  fetchDestinations: async () => {
    set({ loadingDestination: true, errorDestinations: null })

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}alldestinations`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (!res.ok) {
        throw new Error("Failed to fetch destinations")
      }

      const data = await res.json()

      set({
        destinations: data.destinations ?? data,
        loadingDestination: false,
      })
    } catch (error: any) {
      set({
        errorDestinations: error.message,
        loadingDestination: false,
      })
    }
  },

  //------------------------------------
  // 📌 Fetch single destination by slug
  //------------------------------------
  fetchDestinationBySlug: async (slug: string) => {
    set({ loadingDestination: true, errorDestination: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}destinations/${slug}`)
      if (!res.ok) throw new Error(`Failed to load destination: ${res.status}`)

      const data = await res.json()
      set({ destination: data.destination })
    } catch (err: any) {
      set({ errorDestination: err.message })
    } finally {
      set({ loadingDestination: false })
    }
  },

  //------------------------------------
  // 🍽 Restaurants
  //------------------------------------
  fetchRestaurantsBySlug: async (slug: string) => {
    set({ loadingRestaurants: true, errorRestaurants: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}restaurants?destination_id=${slug}`)
      if (!res.ok) throw new Error("Failed to fetch restaurants")

      const data = await res.json()
      set({ restaurants: data })
    } catch (err: any) {
      set({ errorRestaurants: err.message })
    } finally {
      set({ loadingRestaurants: false })
    }
  },

  //------------------------------------
  // 🏡 Maisons d’hôtes
  //------------------------------------
  fetchMaisonsBySlug: async (slug: string) => {
    set({ loadingMaisons: true, errorMaisons: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}maison?destination_id=${slug}`)
      if (!res.ok) throw new Error("Failed to fetch maisons")

      const data = await res.json()
      set({ maisons: data })
    } catch (err: any) {
      set({ errorMaisons: err.message })
    } finally {
      set({ loadingMaisons: false })
    }
  },

  //------------------------------------
  // 🏖 Weekends
  //------------------------------------
  fetchWeekendsBySlug: async (slug: string) => {
    set({ loadingWeekends: true, errorWeekends: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}weekend?destination_id=${slug}`)
      if (!res.ok) throw new Error("Failed to fetch weekends")

      const data = await res.json()
      set({ weekends: data })
    } catch (err: any) {
      set({ errorWeekends: err.message })
    } finally {
      set({ loadingWeekends: false })
    }
  },

  //------------------------------------
  // 🖼 Musées
  //------------------------------------
  fetchMuseesBySlug: async (slug: string) => {
    set({ loadingMusees: true, errorMusees: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}musees?destination_id=${slug}`)
      if (!res.ok) throw new Error("Failed to fetch musees")

      const data = await res.json()
      set({ musees: data })
    } catch (err: any) {
      set({ errorMusees: err.message })
    } finally {
      set({ loadingMusees: false })
    }
  },

  //------------------------------------
  // 🎉 Events
  //------------------------------------
  fetchAllEventsBySlug: async (slug: string) => {
    set({ loadingAllEvents: true, errorAllEvents: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}allevent?destination_id=${slug}`)
      if (!res.ok) throw new Error("Failed to fetch events")

      const data = await res.json()
      set({ allEvents: data })
    } catch (err: any) {
      set({ errorAllEvents: err.message })
    } finally {
      set({ loadingAllEvents: false })
    }
  },

  //------------------------------------
  // 🎉 Hotels
  //------------------------------------
  fetchAllHotelsBySlug: async (slug: string) => {
    set({ loadingAllEvents: true, errorAllEvents: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}allevent?destination_id=${slug}`)
      if (!res.ok) throw new Error("Failed to fetch events")

      const data = await res.json()
      set({ allEvents: data })
    } catch (err: any) {
      set({ errorAllEvents: err.message })
    } finally {
      set({ loadingAllEvents: false })
    }
  },

  //------------------------------------
  // 🌟 Fetch EVERYTHING by slug (optimized)
  //------------------------------------
  fetchAllBySlug: async (slug: string) => {
    set({
      loadingDestination: true,
      loadingRestaurants: true,
      loadingMaisons: true,
      loadingWeekends: true,
      loadingMusees: true,
      loadingAllEvents: true,

      errorDestination: null,
      errorRestaurants: null,
      errorMaisons: null,
      errorWeekends: null,
      errorMusees: null,
      errorAllEvents: null,
    })

    try {
      const [
        destRes,
        restRes,
        maisonsRes,
        weekendsRes,
        museesRes,
        eventsRes,
        hotelsRes,
      ] = await Promise.all([
        fetch(`${process.env.NEXT_PUBLIC_API_URL}destinations/${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}restaurants?destination_id=${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}maison?destination_id=${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}weekend?destination_id=${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}musees?destination_id=${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}allevent?destination_id=${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}hotels?destination_id=${slug}`),
      ])

      if (!destRes.ok) throw new Error("Destination not found")

      const destData = await destRes.json()
      const restData = await restRes.json()
      const maisonsData = await maisonsRes.json()
      const weekendsData = await weekendsRes.json()
      const museesData = await museesRes.json()
      const eventsData = await eventsRes.json()
      const hotelsData= await hotelsRes.json()

      set({
        destination: destData.destination,
        restaurants: restData.restaurants.data,
        maisons: maisonsData.maisons.data,
        weekends: weekendsData.events.data,
        musees: museesData.musees.data,
        allEvents: eventsData.events.data,
        hotels: hotelsData.hotels.data
      })
    } catch (err: any) {
      const error = err.message || "Failed to load data"
      set({
        errorDestination: error,
        errorRestaurants: error,
        errorMaisons: error,
        errorWeekends: error,
        errorMusees: error,
        errorAllEvents: error,
        errorAllHotels: error
      })
    } finally {
      set({
        loadingDestination: false,
        loadingRestaurants: false,
        loadingMaisons: false,
        loadingWeekends: false,
        loadingMusees: false,
        loadingAllEvents: false,
        loadingAllHotels: false,
      })
    }
  }

}))
